import { createRef, Component } from 'react';
import { isEqual } from 'lodash';
import styled from 'styled-components';
import { ArdoqId } from '@ardoq/api-types';
import { ComponentMatrixGroup } from '../../types';
import {
  ComponentMatrix2Commands,
  ComponentMatrix2Group,
  ViewpointModeComponentMatrixViewModel,
} from '../types';
import { ComponentMatrixHeaderCell } from './componentMatrixHeaderCell';
import { getScrollbarWidth } from '@ardoq/common-helpers';
import { COLUMN_HEADER_STRIPE_HEIGHT } from '../consts';

const parentKey = (column: ComponentMatrixGroup) =>
  column.address.slice(0, -1).join('\0');

const isSameGroup = (a: ComponentMatrixGroup, b: ComponentMatrixGroup) =>
  parentKey(a) === parentKey(b);

type SiblingPosition = { isFirst: boolean; isLast: boolean };

const computeSiblingPositions = (
  columns: ComponentMatrixGroup[]
): SiblingPosition[] =>
  columns.map((column, index) => ({
    isFirst: index === 0 || !isSameGroup(columns[index - 1], column),
    isLast:
      index === columns.length - 1 || !isSameGroup(column, columns[index + 1]),
  }));

const ComponentMatrixColumnHeadersAreaContainer = styled.div`
  grid-row: 1;
  grid-column: 2;
  display: grid;
  overflow: hidden;
  margin-right: ${getScrollbarWidth()}px;
`;
type ComponentMatrixColumnHeadersAreaProperties = {
  viewModel: ViewpointModeComponentMatrixViewModel;
  columnWidths: number[];
  contextComponentId: ArdoqId | null;
  commands: ComponentMatrix2Commands;
};

const isColumnEffectivelyEqual = (
  a: ComponentMatrix2Group,
  b: ComponentMatrix2Group
) =>
  a.label === b.label &&
  a.isExpanded === b.isExpanded &&
  a.isHidden === b.isHidden &&
  a.fillColor === b.fillColor &&
  isEqual(a.otherLabels, b.otherLabels);

const isColumnLevelEffectivelyEqual = (
  a: ComponentMatrix2Group[],
  b: ComponentMatrix2Group[]
) =>
  a.length === b.length &&
  a.every((column, columnIndex) =>
    isColumnEffectivelyEqual(column, b[columnIndex])
  );

const hasComponentId = (
  columns: ComponentMatrixGroup[],
  componentId: string | null
): boolean =>
  componentId !== null &&
  columns.some(
    column =>
      column.label === componentId ||
      hasComponentId([...column.children.values()], componentId)
  );

class ComponentMatrixColumnHeadersArea extends Component<ComponentMatrixColumnHeadersAreaProperties> {
  private containerReference = createRef<HTMLDivElement>();
  shouldComponentUpdate(nextProps: ComponentMatrixColumnHeadersAreaProperties) {
    if (this.props.commands !== nextProps.commands) {
      return true;
    }
    const columnWidthsChanged = !isEqual(
      this.props.columnWidths,
      nextProps.columnWidths
    );
    if (columnWidthsChanged) {
      return true;
    }
    const { viewModel } = this.props;
    const { viewModel: nextViewModel } = nextProps;

    const hoveredComponentChanged =
      viewModel.hoveredComponentId !== nextViewModel.hoveredComponentId;
    if (hoveredComponentChanged) {
      const wasPreviousHoveredComponentInView = hasComponentId(
        [...viewModel.columns.values()],
        viewModel.hoveredComponentId
      );
      if (wasPreviousHoveredComponentInView) {
        return true;
      }
      const isNewHoveredComponentInView = hasComponentId(
        [...nextProps.viewModel.columns.values()],
        nextViewModel.hoveredComponentId
      );
      if (isNewHoveredComponentInView) {
        return true;
      }
    }

    const focusedComponentChanged =
      viewModel.focusedComponentId !== nextViewModel.focusedComponentId;
    if (focusedComponentChanged) {
      const wasPreviousFocusedComponentInView = hasComponentId(
        [...viewModel.columns.values()],
        viewModel.focusedComponentId
      );
      if (wasPreviousFocusedComponentInView) {
        return true;
      }
      const isNewFocusedComponentInView = hasComponentId(
        [...nextProps.viewModel.columns.values()],
        nextViewModel.focusedComponentId
      );
      if (isNewFocusedComponentInView) {
        return true;
      }
    }

    const contextComponentChanged =
      this.props.contextComponentId !== nextProps.contextComponentId;
    if (contextComponentChanged) {
      const wasPreviousContextInView = hasComponentId(
        [...viewModel.columns.values()],
        this.props.contextComponentId
      );
      if (wasPreviousContextInView) {
        return true;
      }
      const isNewContextInView = hasComponentId(
        [...nextProps.viewModel.columns.values()],
        nextProps.contextComponentId
      );
      if (isNewContextInView) {
        return true;
      }
    }

    return (
      viewModel.columnsByDepth.length !== nextViewModel.columnsByDepth.length ||
      !viewModel.columnsByDepth.every((columnLevel, columnLevelIndex) =>
        isColumnLevelEffectivelyEqual(
          columnLevel,
          nextViewModel.columnsByDepth[columnLevelIndex]
        )
      )
    );
  }
  setScrollLeft(value: number) {
    if (!this.containerReference.current) {
      return;
    }
    this.containerReference.current.scrollLeft = value;
  }
  render() {
    const { viewModel, columnWidths, contextComponentId, commands } =
      this.props;
    const {
      measurements: {
        columnHeaderWidths,
        columnHeadersAreaHeight,
        columnHeadersAreaGridTemplateRows,
        columnHeaderLevelStripeHeights,
      },
      columnsByDepth,
      hoveredComponentId,
      focusedComponentId,
    } = viewModel;
    const noHeaders =
      columnHeaderWidths.length === 1 && columnHeaderWidths[0] === 0;
    if (noHeaders) {
      return null;
    }
    const columnDepth = columnsByDepth.length;
    return (
      <ComponentMatrixColumnHeadersAreaContainer
        ref={this.containerReference}
        style={{
          gridTemplateColumns: columnWidths
            .map(width => `${width}px`)
            .join(' '),
          gridTemplateRows: columnHeadersAreaGridTemplateRows,
          // `min-height` rather than `height` so the outer grid row can grow
          // when the level-headers area on the left is taller; the last row
          // of `gridTemplateRows` uses `minmax(<h>, 1fr)` to absorb the
          // extra space (ARD-31376).
          minHeight: columnHeadersAreaHeight,
        }}
      >
        {viewModel.columnsByDepthWithGridInfo.map(
          (levelColumns, levelIndex) => {
            const siblingPositions = computeSiblingPositions(levelColumns);
            const stripeHeight =
              columnHeaderLevelStripeHeights[levelIndex] ??
              COLUMN_HEADER_STRIPE_HEIGHT;
            return levelColumns.map((column, index) => (
              <ComponentMatrixHeaderCell
                column={column}
                key={column.gridColumn}
                columnDepth={columnDepth}
                gridColumn={column.gridColumn}
                colSpan={column.colSpan}
                isFirstSibling={siblingPositions[index].isFirst}
                isLastSibling={siblingPositions[index].isLast}
                stripeHeight={stripeHeight}
                hoveredComponentId={hoveredComponentId}
                focusedComponentId={focusedComponentId}
                contextComponentId={contextComponentId}
                commands={commands}
              />
            ));
          }
        )}
      </ComponentMatrixColumnHeadersAreaContainer>
    );
  }
}
export { ComponentMatrixColumnHeadersArea };
